import React, { useState, useRef } from 'react';
import {
  FiCopy, FiLayers, FiScissors, FiRefreshCw, FiUpload, FiX, FiCheck,
  FiChevronUp, FiChevronDown, FiDownload, FiAlertTriangle,
} from 'react-icons/fi';
import { PDFDocument } from 'pdf-lib';
import JSZip from 'jszip';
import { gerarMiniaturas } from './pdfjsWorker';
import { converterParaPdfA } from '../../services/pdf';
import styles from './Pdf.module.css';

const ABAS = [
  { id: 'juntar',  label: 'Juntar',  icon: FiLayers },
  { id: 'separar', label: 'Separar', icon: FiScissors },
];

/* dispara o download de um arquivo no navegador sem deixar rastro no servidor —
   tudo processado e descartado na hora, nada fica guardado */
function baixarArquivo(bytes, nomeArquivo, tipo = 'application/pdf') {
  const blob = new Blob([bytes], { type: tipo });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function nomeSemExtensao(nome) {
  return nome.replace(/\.pdf$/i, '');
}

function rotuloBotao(totalArquivos, converterPdfA) {
  if (totalArquivos === 1) return converterPdfA ? 'Converter e baixar' : 'Baixar';
  return converterPdfA ? 'Juntar, converter e baixar' : 'Juntar e baixar';
}

/* ── Juntar: uma PDF só pode ser apenas convertida; duas ou mais viram uma
   só, na ordem escolhida — tudo no navegador, nenhum arquivo sai da
   máquina do usuário. ── */
function JuntarTab() {
  const [arquivos, setArquivos] = useState([]);
  const [converterPdfA, setConverterPdfA] = useState(true);
  const [processando, setProcessando] = useState(false);
  const [etapa, setEtapa] = useState('');
  const [erro, setErro] = useState('');
  const inputRef = useRef(null);

  function adicionarArquivos(fileList) {
    const novos = Array.from(fileList).filter(f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
    setArquivos(prev => [...prev, ...novos]);
    setErro('');
  }

  function remover(idx) {
    setArquivos(prev => prev.filter((_, i) => i !== idx));
  }

  function mover(idx, direcao) {
    setArquivos(prev => {
      const lista = [...prev];
      const alvo = idx + direcao;
      if (alvo < 0 || alvo >= lista.length) return lista;
      [lista[idx], lista[alvo]] = [lista[alvo], lista[idx]];
      return lista;
    });
  }

  async function juntarEBaixar() {
    if (arquivos.length < 1) { setErro('Selecione pelo menos 1 arquivo PDF.'); return; }
    const unico = arquivos.length === 1;
    const nomeBase = unico ? nomeSemExtensao(arquivos[0].name) : 'documentos-unidos';
    setProcessando(true); setErro('');
    try {
      setEtapa(unico ? 'Preparando...' : 'Juntando...');
      const destino = await PDFDocument.create();
      for (const arquivo of arquivos) {
        const bytes = await arquivo.arrayBuffer();
        const origem = await PDFDocument.load(bytes);
        const paginas = await destino.copyPages(origem, origem.getPageIndices());
        paginas.forEach(p => destino.addPage(p));
      }
      const bytesUnidos = await destino.save();

      if (!converterPdfA) {
        baixarArquivo(bytesUnidos, `${nomeBase}.pdf`);
        return;
      }

      setEtapa('Convertendo pra PDF/A...');
      const blobUnido = new Blob([bytesUnidos], { type: 'application/pdf' });
      const bytesConvertidos = await converterParaPdfA(blobUnido);
      baixarArquivo(bytesConvertidos, `${nomeBase}-pdfa.pdf`);
    } catch (e) {
      console.error('Falha ao juntar PDFs:', e);
      if (/encrypted/i.test(e?.message || '')) {
        setErro('Um dos PDFs está protegido por senha. Remova a proteção antes de enviar.');
      } else {
        setErro('Não foi possível processar esses arquivos — confira se todos são PDFs válidos.');
      }
    } finally {
      setProcessando(false);
      setEtapa('');
    }
  }

  return (
    <div>
      <p className={styles.descricao}>Selecione um PDF pra só converter, ou vários na ordem em que devem ser unidos (dá pra reordenar depois). Se marcar a conversão pra PDF/A, ela roda no nosso próprio servidor — o arquivo nunca é enviado a site externo, e não fica guardado depois.</p>

      <button type="button" className={styles.btnUpload} onClick={() => inputRef.current?.click()}>
        <FiUpload size={14} /> Selecionar PDFs
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        style={{ display: 'none' }}
        onChange={e => { adicionarArquivos(e.target.files); e.target.value = ''; }}
      />

      {arquivos.length > 0 && (
        <ul className={styles.listaArquivos}>
          {arquivos.map((f, i) => (
            <li key={i} className={styles.linhaArquivo}>
              <span className={styles.numeroArquivo}>{i + 1}</span>
              <span className={styles.nomeArquivo}>{f.name}</span>
              <span className={styles.acoesArquivo}>
                <button type="button" className={styles.btnIcone} onClick={() => mover(i, -1)} disabled={i === 0} title="Mover pra cima">
                  <FiChevronUp size={13} />
                </button>
                <button type="button" className={styles.btnIcone} onClick={() => mover(i, 1)} disabled={i === arquivos.length - 1} title="Mover pra baixo">
                  <FiChevronDown size={13} />
                </button>
                <button type="button" className={styles.btnIconeRemover} onClick={() => remover(i)} title="Remover">
                  <FiX size={13} />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer', margin: '12px 0' }}>
        <input type="checkbox" checked={converterPdfA} onChange={e => setConverterPdfA(e.target.checked)} />
        <FiRefreshCw size={13} />
        Converter para PDF/A (padrão exigido pra protocolar na Junta Comercial)
      </label>

      {erro && <p className={styles.erro}><FiAlertTriangle size={13} /> {erro}</p>}

      <button
        type="button"
        className={styles.btnAcao}
        onClick={juntarEBaixar}
        disabled={processando || arquivos.length < 1}
      >
        <FiDownload size={14} /> {processando ? etapa : rotuloBotao(arquivos.length, converterPdfA)}
      </button>
    </div>
  );
}

/* ── Separar: extrai páginas específicas de um PDF pra um novo arquivo —
   tudo no navegador, nenhum arquivo sai da máquina do usuário. ── */
function nomePaginaPadrao(pagina) {
  return `pagina-${String(pagina).padStart(3, '0')}`;
}

function sanitizarNomeArquivo(nome) {
  return nome.replace(/[\\/:*?"<>|]/g, '-').trim();
}

function SepararTab() {
  const [arquivo, setArquivo] = useState(null);
  const [miniaturas, setMiniaturas] = useState([]);
  const [selecionadas, setSelecionadas] = useState(new Set());
  const [nomes, setNomes] = useState({});
  const [carregandoMiniaturas, setCarregandoMiniaturas] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [erro, setErro] = useState('');
  const inputRef = useRef(null);

  async function selecionarArquivo(file) {
    if (!file) return;
    setErro(''); setArquivo(file); setMiniaturas([]); setSelecionadas(new Set()); setNomes({});
    setCarregandoMiniaturas(true);
    try {
      const bytes = await file.arrayBuffer();
      const lista = await gerarMiniaturas(bytes);
      setMiniaturas(lista);
    } catch (e) {
      console.error('Falha ao abrir PDF em SepararTab:', e);
      if (e?.name === 'PasswordException') {
        setErro('Esse PDF está protegido por senha. Remova a proteção (por exemplo, abrindo e salvando uma cópia sem senha) antes de enviar.');
      } else {
        setErro('Não foi possível abrir esse PDF — confira se o arquivo não está corrompido.');
      }
      setArquivo(null);
    } finally {
      setCarregandoMiniaturas(false);
    }
  }

  function alternarPagina(pagina) {
    setSelecionadas(prev => {
      const next = new Set(prev);
      if (next.has(pagina)) next.delete(pagina);
      else next.add(pagina);
      return next;
    });
  }

  function selecionarTodas()  { setSelecionadas(new Set(miniaturas.map(m => m.pagina))); }
  function selecionarNenhuma() { setSelecionadas(new Set()); }
  function selecionarImpares() { setSelecionadas(new Set(miniaturas.map(m => m.pagina).filter(p => p % 2 === 1))); }
  function selecionarPares()   { setSelecionadas(new Set(miniaturas.map(m => m.pagina).filter(p => p % 2 === 0))); }

  /* editar o nome da primeira página replica o mesmo nome pras demais,
     cada uma com um numerozinho no final pra não ficarem idênticas —
     dá pra ajustar cada uma individualmente depois */
  function alterarNome(pagina, valor) {
    setNomes(prev => {
      const next = { ...prev, [pagina]: valor };
      const paginasOrdenadas = [...selecionadas].sort((a, b) => a - b);
      if (pagina === paginasOrdenadas[0] && valor.trim()) {
        paginasOrdenadas.slice(1).forEach((p, i) => { next[p] = `${valor}-${i + 2}`; });
      }
      return next;
    });
  }

  async function separarEBaixar() {
    if (!arquivo || selecionadas.size === 0) return;
    setProcessando(true); setErro('');
    try {
      const bytesOrigem = await arquivo.arrayBuffer();
      const origem = await PDFDocument.load(bytesOrigem);
      const paginasOrdenadas = [...selecionadas].sort((a, b) => a - b);

      const zip = new JSZip();
      const nomesUsados = new Set();
      for (const pagina of paginasOrdenadas) {
        const destino = await PDFDocument.create();
        const [copiada] = await destino.copyPages(origem, [pagina - 1]);
        destino.addPage(copiada);
        const bytes = await destino.save();

        let nome = sanitizarNomeArquivo(nomes[pagina] || '') || nomePaginaPadrao(pagina);
        if (nomesUsados.has(nome)) nome = `${nome}-${nomePaginaPadrao(pagina)}`;
        nomesUsados.add(nome);
        zip.file(`${nome}.pdf`, bytes);
      }
      const bytesZip = await zip.generateAsync({ type: 'uint8array' });
      baixarArquivo(bytesZip, `${nomeSemExtensao(arquivo.name)}-paginas.zip`, 'application/zip');
    } catch (e) {
      console.error('Falha ao separar páginas do PDF:', e);
      setErro('Não foi possível separar essas páginas.');
    } finally {
      setProcessando(false);
    }
  }

  return (
    <div>
      <p className={styles.descricao}>Selecione um PDF, clique nas páginas que quer separar e baixe cada uma como um arquivo PDF individual (dentro de um .zip).</p>

      <button type="button" className={styles.btnUpload} onClick={() => inputRef.current?.click()}>
        <FiUpload size={14} /> Selecionar PDF
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        style={{ display: 'none' }}
        onChange={e => { selecionarArquivo(e.target.files?.[0]); e.target.value = ''; }}
      />

      {carregandoMiniaturas && <p className={styles.descricao}>Carregando páginas...</p>}

      {miniaturas.length > 0 && (
        <>
          <div className={styles.controlesSelecao}>
            <span className={styles.nomeArquivo}>{arquivo.name} — {miniaturas.length} página{miniaturas.length !== 1 ? 's' : ''}</span>
            <span className={styles.controlesBotoes}>
              <button type="button" className={styles.btnSelecao} onClick={selecionarTodas}>Todas</button>
              <button type="button" className={styles.btnSelecao} onClick={selecionarImpares}>Ímpares</button>
              <button type="button" className={styles.btnSelecao} onClick={selecionarPares}>Pares</button>
              <button type="button" className={styles.btnSelecao} onClick={selecionarNenhuma}>Nenhuma</button>
            </span>
          </div>

          <div className={styles.gradeMiniaturas}>
            {miniaturas.map(m => (
              <button
                type="button"
                key={m.pagina}
                className={`${styles.miniatura} ${selecionadas.has(m.pagina) ? styles.miniaturaSelecionada : ''}`}
                onClick={() => alternarPagina(m.pagina)}
              >
                <img src={m.dataUrl} alt={`Página ${m.pagina}`} />
                <span className={styles.miniaturaNumero}>{m.pagina}</span>
                {selecionadas.has(m.pagina) && <span className={styles.miniaturaCheck}><FiCheck size={12} /></span>}
              </button>
            ))}
          </div>

          {selecionadas.size > 0 && (
            <>
              <span className={styles.campoLabel}>Nome de cada arquivo ao baixar</span>
              <ul className={styles.listaArquivos}>
                {[...selecionadas].sort((a, b) => a - b).map(pagina => (
                  <li key={pagina} className={styles.linhaArquivo}>
                    <span className={styles.numeroArquivo}>{pagina}</span>
                    <input
                      type="text"
                      className={`${styles.campoInput} ${styles.inputNomeArquivo}`}
                      value={nomes[pagina] ?? ''}
                      placeholder={nomePaginaPadrao(pagina)}
                      onChange={e => alterarNome(pagina, e.target.value)}
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {erro && <p className={styles.erro}><FiAlertTriangle size={13} /> {erro}</p>}

      <button
        type="button"
        className={styles.btnAcao}
        onClick={separarEBaixar}
        disabled={processando || selecionadas.size === 0}
      >
        <FiDownload size={14} /> {processando ? 'Separando...' : `Separar e baixar (.zip)${selecionadas.size ? ` — ${selecionadas.size} página${selecionadas.size !== 1 ? 's' : ''}` : ''}`}
      </button>
    </div>
  );
}

function Pdf() {
  const [abaAtiva, setAbaAtiva] = useState('juntar');

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcone}>
          <FiCopy size={18} />
        </div>
        <div>
          <h1 className={styles.titulo}>PDF</h1>
          <p className={styles.subtitulo}>Juntar ou converter (PDF/A opcional) e separar arquivos PDF — processado localmente, nada fica guardado</p>
        </div>
      </div>

      <div className={styles.tabBar}>
        {ABAS.map(aba => {
          const Icone = aba.icon;
          return (
            <button
              key={aba.id}
              type="button"
              className={`${styles.tab} ${abaAtiva === aba.id ? styles.tabAtiva : ''}`}
              onClick={() => setAbaAtiva(aba.id)}
            >
              <Icone size={15} />
              {aba.label}
            </button>
          );
        })}
      </div>

      <div className={styles.conteudo}>
        {abaAtiva === 'juntar'  && <JuntarTab />}
        {abaAtiva === 'separar' && <SepararTab />}
      </div>
    </div>
  );
}

export default Pdf;
